import type { ThrowableId } from '@big2/shared';

/** Renders rich, non-emoji visual illustration of the flying projectile */
export function ProjectileGraphic({ id }: { id: ThrowableId }) {
  switch (id) {
    case 'tea':
      // Steaming traditional Mongolian teapot / kettle spilling hot milk tea
      return (
        <svg className="projectile-svg" width="56" height="56" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="28" fill="rgba(245, 158, 11, 0.25)" filter="blur(4px)" />
          {/* Kettle Body */}
          <path d="M18 26 C18 20, 46 20, 46 26 L44 48 C44 54, 20 54, 20 48 Z" fill="#b45309" stroke="#fef3c7" strokeWidth="2" />
          <ellipse cx="32" cy="26" rx="14" ry="4" fill="#d97706" />
          {/* Spout pouring tea */}
          <path d="M44 32 C52 28, 56 22, 58 18 L54 18 C52 22, 48 28, 43 31 Z" fill="#b45309" stroke="#fef3c7" strokeWidth="1.5" />
          {/* Pouring stream droplets */}
          <circle cx="59" cy="18" r="2.5" fill="#fde68a" />
          <circle cx="62" cy="24" r="2" fill="#fde68a" />
          <circle cx="60" cy="30" r="1.8" fill="#fde68a" />
          {/* Kettle Handle */}
          <path d="M19 28 C12 28, 12 44, 20 46" fill="none" stroke="#fef3c7" strokeWidth="3" strokeLinecap="round" />
          {/* Hot Steam Wiggles */}
          <path d="M28 16 Q30 10 28 4" stroke="#fef3c7" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
          <path d="M34 14 Q36 8 34 2" stroke="#fef3c7" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
        </svg>
      );

    case 'slipper':
      // Blue/Red felt & rubber home slipper (Гэрийн улавч)
      return (
        <svg className="projectile-svg" width="56" height="56" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="26" fill="rgba(239, 68, 68, 0.25)" filter="blur(4px)" />
          {/* Slipper Sole */}
          <path d="M16 14 C22 8, 42 12, 48 20 C54 28, 48 52, 38 56 C28 60, 18 52, 14 38 C10 24, 10 20, 16 14 Z" fill="#1e3a8a" stroke="#ffffff" strokeWidth="2.5" />
          {/* Insole */}
          <path d="M18 18 C24 13, 39 16, 44 23 C49 30, 44 48, 36 51 C28 54, 20 48, 17 36 C14 25, 13 22, 18 18 Z" fill="#3b82f6" />
          {/* Slipper Strap with traditional ornament */}
          <path d="M16 26 C22 18, 42 22, 46 32 C40 38, 22 36, 16 26 Z" fill="#ef4444" stroke="#fef08a" strokeWidth="2" />
          <circle cx="31" cy="28" r="3" fill="#fde047" />
        </svg>
      );

    case 'egg':
      // Fresh raw egg flying with speed streaks
      return (
        <svg className="projectile-svg" width="56" height="56" viewBox="0 0 64 64" fill="none">
          <ellipse cx="32" cy="32" rx="18" ry="24" fill="#ffedd5" stroke="#ea580c" strokeWidth="2.5" transform="rotate(-25 32 32)" />
          <ellipse cx="28" cy="28" rx="8" ry="12" fill="#ffffff" opacity="0.6" transform="rotate(-25 28 28)" />
          {/* Motion lines */}
          <line x1="8" y1="48" x2="16" y2="44" stroke="#fdba74" strokeWidth="2" strokeLinecap="round" />
          <line x1="12" y1="56" x2="22" y2="52" stroke="#fdba74" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );

    case 'airag':
      // Traditional Mongolian silver/wood bowl (Хул) with Khatag silk
      return (
        <svg className="projectile-svg" width="56" height="56" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="28" fill="rgba(56, 189, 248, 0.3)" filter="blur(4px)" />
          {/* Blue Silk Khatag flying behind */}
          <path d="M8 20 C18 12, 18 48, 8 44 C2 40, 2 24, 8 20 Z" fill="#0284c7" stroke="#38bdf8" strokeWidth="1.5" />
          {/* Bowl Body */}
          <path d="M16 24 C16 42, 24 50, 36 50 C48 50, 56 42, 56 24 Z" fill="#78350f" stroke="#f59e0b" strokeWidth="2.5" />
          {/* Silver Inlay Ulzii Pattern Band */}
          <ellipse cx="36" cy="24" rx="20" ry="7" fill="#f8fafc" stroke="#f59e0b" strokeWidth="2" />
          <ellipse cx="36" cy="24" rx="16" ry="4.5" fill="#f1f5f9" />
          {/* Frothy Airag Spill */}
          <circle cx="48" cy="18" r="3" fill="#ffffff" />
          <circle cx="54" cy="22" r="2.5" fill="#ffffff" />
        </svg>
      );

    case 'cash':
      // Bound stack of Mongolian ₮20,000 notes flying
      return (
        <svg className="projectile-svg" width="56" height="56" viewBox="0 0 64 64" fill="none">
          {/* Under Bill */}
          <rect x="14" y="24" width="38" height="24" rx="3" fill="#059669" stroke="#a7f3d0" strokeWidth="1.5" transform="rotate(-10 33 36)" />
          {/* Top Bill */}
          <rect x="12" y="20" width="38" height="24" rx="3" fill="#10b981" stroke="#ffffff" strokeWidth="2" />
          {/* Banknote details */}
          <circle cx="31" cy="32" r="6" fill="#047857" stroke="#fde047" strokeWidth="1.5" />
          <text x="27" y="36" fill="#fde047" fontSize="10" fontWeight="900">₮</text>
          <text x="14" y="28" fill="#ffffff" fontSize="6" fontWeight="bold">20K</text>
          <text x="40" y="41" fill="#ffffff" fontSize="6" fontWeight="bold">20K</text>
        </svg>
      );

    case 'shagai':
      // 3D Mongolian Knucklebone (Шагай - морь/хонь)
      return (
        <svg className="projectile-svg" width="56" height="56" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="26" fill="rgba(254, 240, 138, 0.25)" filter="blur(4px)" />
          {/* Bone structure */}
          <path d="M22 16 C28 14, 42 16, 46 22 C50 28, 48 38, 44 44 C40 50, 26 52, 20 46 C14 40, 16 28, 22 16 Z" fill="#fef3c7" stroke="#d97706" strokeWidth="2.5" />
          {/* Grooves & ridges */}
          <path d="M28 20 C34 22, 36 34, 30 42" stroke="#b45309" strokeWidth="2" strokeLinecap="round" />
          <path d="M38 24 C40 30, 40 36, 36 42" stroke="#b45309" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="33" cy="28" r="2" fill="#d97706" />
        </svg>
      );
  }
}

/** Renders massive animated impact graphic covering target's avatar */
export function ImpactGraphic({ id, isMn = true }: { id: ThrowableId; isMn?: boolean }) {
  switch (id) {
    case 'tea':
      return (
        <div className="impact-container impact-tea-stage">
          {/* Big Splashing Milk Tea Wave */}
          <svg className="impact-tea-svg" width="130" height="130" viewBox="0 0 120 120" fill="none">
            {/* Splash droplets explosion */}
            <circle cx="60" cy="60" r="50" fill="rgba(245, 158, 11, 0.28)" filter="blur(6px)" />
            {/* Main splash splat */}
            <path
              d="M60 20 C70 12, 85 25, 82 38 C95 35, 108 50, 100 65 C112 80, 95 100, 80 95 C75 110, 50 112, 42 98 C25 106, 12 90, 22 75 C8 60, 20 42, 35 45 C32 30, 48 18, 60 20 Z"
              fill="#d97706"
              stroke="#fef3c7"
              strokeWidth="3.5"
            />
            {/* Inner liquid puddle */}
            <path
              d="M60 32 C68 26, 78 35, 75 44 C84 42, 92 52, 86 64 C94 75, 82 90, 70 86 C66 96, 48 98, 42 88 C30 94, 22 82, 30 70 C20 60, 28 48, 38 50 C36 40, 50 32, 60 32 Z"
              fill="#fbbf24"
            />
            {/* Flying tea drops */}
            <circle cx="20" cy="22" r="5" fill="#d97706" stroke="#fff" strokeWidth="1.5" />
            <circle cx="98" cy="24" r="4.5" fill="#d97706" stroke="#fff" strokeWidth="1.5" />
            <circle cx="106" cy="85" r="4" fill="#d97706" stroke="#fff" strokeWidth="1.5" />
            <circle cx="16" cy="88" r="4.5" fill="#d97706" stroke="#fff" strokeWidth="1.5" />
          </svg>
          <div className="impact-caption tea-caption">{isMn ? 'ХАЛУУН ЦАЙ! ♨️' : 'HOT TEA! ♨️'}</div>
        </div>
      );

    case 'slipper':
      return (
        <div className="impact-container impact-slipper-stage">
          {/* Comic Starburst SLAP */}
          <svg className="impact-slipper-svg" width="140" height="140" viewBox="0 0 140 140" fill="none">
            {/* Comic Bang Starburst */}
            <polygon
              points="70,5 86,45 130,30 105,70 135,100 90,105 85,135 60,110 25,130 38,90 5,75 40,55 20,20 60,35"
              fill="#ef4444"
              stroke="#fbbf24"
              strokeWidth="4"
            />
            <polygon
              points="70,18 82,50 115,40 96,70 118,92 84,96 80,118 62,98 35,112 45,82 20,72 46,58 32,32 62,44"
              fill="#fef08a"
            />
            {/* Whacking Slipper */}
            <g transform="translate(42, 35) rotate(-25 28 35)">
              <path d="M12 8 C18 2, 38 6, 44 14 C50 22, 44 46, 34 50 C24 54, 14 46, 10 32 C6 18, 6 14, 12 8 Z" fill="#1e3a8a" stroke="#fff" strokeWidth="2.5" />
              <path d="M12 20 C18 12, 38 16, 42 26 C36 32, 18 30, 12 20 Z" fill="#ef4444" stroke="#fef08a" strokeWidth="2" />
            </g>
          </svg>
          <div className="impact-caption slap-caption">{isMn ? 'ТАШИТАЛ! 💥' : 'SMACK! 💥'}</div>
        </div>
      );

    case 'egg':
      return (
        <div className="impact-container impact-egg-stage">
          {/* Cracked egg with gooey dripping yolk */}
          <svg className="impact-egg-svg" width="130" height="130" viewBox="0 0 120 120" fill="none">
            {/* Egg White Splat */}
            <path
              d="M60 18 C76 10, 92 25, 90 42 C105 45, 112 65, 102 82 C108 98, 88 114, 72 108 C62 118, 42 115, 36 102 C20 108, 8 92, 18 76 C6 60, 16 42, 32 44 C28 28, 45 16, 60 18 Z"
              fill="#ffffff"
              stroke="#fed7aa"
              strokeWidth="3"
            />
            {/* Big Runny Golden Yolk */}
            <circle cx="60" cy="62" r="24" fill="#f59e0b" stroke="#fde047" strokeWidth="3" />
            <ellipse cx="54" cy="54" rx="7" ry="5" fill="#ffffff" opacity="0.75" />
            {/* Broken Egg Shell Fragments */}
            <path d="M22 32 L32 24 L36 36 L26 42 Z" fill="#fed7aa" stroke="#ea580c" strokeWidth="1.5" />
            <path d="M88 88 L98 78 L104 90 L92 96 Z" fill="#fed7aa" stroke="#ea580c" strokeWidth="1.5" />
            {/* Dripping Yolk Trails */}
            <path d="M58 84 Q60 104 62 114" stroke="#f59e0b" strokeWidth="6" strokeLinecap="round" />
            <path d="M68 82 Q72 98 74 106" stroke="#f59e0b" strokeWidth="4.5" strokeLinecap="round" />
          </svg>
          <div className="impact-caption egg-caption">{isMn ? 'ӨНДӨГ НЯЛЛАА! 🍳' : 'EGG SPLAT! 🍳'}</div>
        </div>
      );

    case 'airag':
      return (
        <div className="impact-container impact-airag-stage">
          {/* Splattering foamy Airag with Traditional Silver Bowl */}
          <svg className="impact-airag-svg" width="130" height="130" viewBox="0 0 120 120" fill="none">
            {/* White foamy explosion */}
            <circle cx="60" cy="60" r="50" fill="rgba(56, 189, 248, 0.28)" filter="blur(6px)" />
            <path
              d="M60 16 C78 12, 94 28, 90 46 C108 52, 114 74, 100 90 C104 108, 82 118, 68 110 C54 118, 34 112, 30 96 C12 100, 6 80, 18 66 C8 48, 24 34, 40 38 C42 20, 58 14, 60 16 Z"
              fill="#f8fafc"
              stroke="#38bdf8"
              strokeWidth="3"
            />
            {/* Silver / Gold Bowl in center */}
            <g transform="translate(35, 38)">
              <path d="M8 16 C8 34, 18 42, 28 42 C38 42, 48 34, 48 16 Z" fill="#78350f" stroke="#fbbf24" strokeWidth="2.5" />
              <ellipse cx="28" cy="16" rx="20" ry="7" fill="#ffffff" stroke="#fbbf24" strokeWidth="2" />
            </g>
            {/* Celebration Stars */}
            <text x="18" y="32" fill="#fbbf24" fontSize="18">✨</text>
            <text x="86" y="36" fill="#fbbf24" fontSize="18">✨</text>
            <text x="88" y="94" fill="#38bdf8" fontSize="16">⭐</text>
          </svg>
          <div className="impact-caption airag-caption">{isMn ? 'АЙРГААР МЯЛААЛАА! 🍶' : 'AIRAG SPLASH! 🍶'}</div>
        </div>
      );

    case 'cash':
      return (
        <div className="impact-container impact-cash-stage">
          {/* Exploding banknotes raining everywhere */}
          <div className="cash-rain-particles">
            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
              <span
                key={i}
                className="flutter-bill"
                style={{
                  transform: `rotate(${deg}deg) translate(${35 + (i % 3) * 15}px) rotate(${deg * -0.5}deg)`,
                }}
              >
                ₮
              </span>
            ))}
          </div>
          <svg width="110" height="110" viewBox="0 0 100 100" fill="none">
            <circle cx="50" cy="50" r="40" fill="rgba(16, 185, 129, 0.3)" filter="blur(6px)" />
            {/* Flying bills cluster */}
            <rect x="25" y="35" width="50" height="28" rx="3" fill="#10b981" stroke="#ffffff" strokeWidth="2.5" transform="rotate(-15 50 49)" />
            <circle cx="50" cy="49" r="8" fill="#047857" stroke="#fde047" strokeWidth="2" />
            <text x="45" y="55" fill="#fde047" fontSize="14" fontWeight="900">₮</text>
          </svg>
          <div className="impact-caption cash-caption">{isMn ? 'МӨНГӨӨР САВЛАА! 💸' : 'CASH RAIN! 💸'}</div>
        </div>
      );

    case 'shagai':
      return (
        <div className="impact-container impact-shagai-stage">
          {/* Hard bone impact with comic sparks */}
          <svg className="impact-shagai-svg" width="130" height="130" viewBox="0 0 120 120" fill="none">
            {/* Spark explosion rays */}
            <path d="M60 10 L60 26 M60 94 L60 110 M10 60 L26 60 M94 60 L110 60 M24 24 L36 36 M84 84 L96 96 M24 96 L36 84 M84 36 L96 24" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" />
            {/* Center shockwave */}
            <circle cx="60" cy="60" r="28" fill="#fef3c7" stroke="#ea580c" strokeWidth="3" />
            {/* Shagai in center */}
            <path d="M46 42 C52 38, 68 40, 72 46 C76 52, 74 62, 70 68 C66 74, 52 76, 46 70 C40 64, 40 52, 46 42 Z" fill="#fde047" stroke="#b45309" strokeWidth="2.5" />
            <circle cx="58" cy="54" r="2.5" fill="#b45309" />
          </svg>
          <div className="impact-caption shagai-caption">{isMn ? 'ШАГАЙ НЯСАЛЛАА! 🦴' : 'SHAGAI HIT! 🦴'}</div>
        </div>
      );
  }
}
